/**
 * Session Warning Component
 * Displays a warning modal when user session is about to expire
 */

import React, { useState, useEffect } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { sessionManager } from '@/services/sessionManager';
import { useAuth } from '@/hooks/useAuth';

interface SessionWarningProps {
  onExtend?: () => void;
  onLogout?: () => void;
}

export const SessionWarning: React.FC<SessionWarningProps> = ({
  onExtend,
  onLogout,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const { logout } = useAuth();

  useEffect(() => {
    // Set up session monitoring with warning callback
    const handleWarning = (remaining: number) => {
      setTimeRemaining(remaining);
      setIsVisible(true);
    };

    const handleExpiration = () => {
      setIsVisible(false);
      if (onLogout) {
        onLogout();
      }
    };

    sessionManager.startSession(handleExpiration, handleWarning);

    // Update countdown timer
    const timer = setInterval(() => {
      if (isVisible) {
        const sessionInfo = sessionManager.getSessionInfo();
        if (sessionInfo.timeRemaining <= 0) {
          setIsVisible(false);
        } else {
          setTimeRemaining(sessionInfo.timeRemaining);
        }
      }
    }, 1000);

    return () => {
      clearInterval(timer);
      sessionManager.stopSession();
    };
  }, [onLogout, isVisible]);

  const handleExtendSession = () => {
    sessionManager.extendSession();
    setIsVisible(false);
    if (onExtend) {
      onExtend();
    }
  };

  const handleLogout = async () => {
    setIsVisible(false);
    try {
      await logout();
      if (onLogout) {
        onLogout();
      }
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  const formatTime = (milliseconds: number): string => {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (!isVisible) return null;

  return (
    <Modal
      isOpen={isVisible}
      onClose={() => {}} // Prevent closing without action
      title="Sesión por Expirar"
      size="sm"
    >
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
          <svg
            className="h-6 w-6 text-yellow-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>

        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Tu sesión expirará pronto
        </h3>

        <p className="text-sm text-gray-500 mb-4">
          Por seguridad, tu sesión se cerrará automáticamente por inactividad.
        </p>

        <div className="text-2xl font-bold text-red-600 mb-6">
          {formatTime(timeRemaining)}
        </div>

        <p className="text-sm text-gray-600 mb-6">
          ¿Deseas extender tu sesión o cerrarla ahora?
        </p>

        <div className="flex space-x-3 justify-center">
          <Button
            variant="secondary"
            onClick={handleLogout}
            className="px-4 py-2"
          >
            Cerrar Sesión
          </Button>
          <Button
            variant="primary"
            onClick={handleExtendSession}
            className="px-4 py-2"
          >
            Extender Sesión
          </Button>
        </div>
      </div>
    </Modal>
  );
};

/**
 * Hook for session warning management
 */
export const useSessionWarning = () => {
  const [showWarning, setShowWarning] = useState(false);

  const handleWarning = () => {
    setShowWarning(true);
  };

  const handleExtend = () => {
    setShowWarning(false);
  };

  const handleLogout = () => {
    setShowWarning(false);
  };

  return {
    showWarning,
    handleWarning,
    handleExtend,
    handleLogout,
    SessionWarningComponent: () => (
      <SessionWarning onExtend={handleExtend} onLogout={handleLogout} />
    ),
  };
};
